import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { VacancySyncState } from "../../api/types";
import { useRepairVacancies } from "../../api/useRepairVacancies";
import { useResyncVacancies } from "../../api/useResyncVacancies";
import { parseVacancySyncState } from "../../utils";

import Icons from "@/components/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { Button } from "@/components/ui/v2/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";

interface VacancySyncOverviewHeaderProps {
  clientId?: string;
  integrationName: string;
}

export function VacancySyncOverviewHeader({
  clientId,
  integrationName,
}: VacancySyncOverviewHeaderProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const syncStateParam = searchParams.get("syncState");
  const syncStateFilter = syncStateParam
    ? parseVacancySyncState(syncStateParam)
    : null;

  const handleSyncStateChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete("syncState");
    } else {
      newParams.set("syncState", value);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const repairVacanciesMutation = useRepairVacancies({
    integrationName,
  });

  const resyncVacanciesMutation = useResyncVacancies({
    integrationName,
  });

  const [showResyncConfirm, setShowResyncConfirm] = useState(false);

  const handleResyncConfirm = () => {
    if (clientId) {
      resyncVacanciesMutation.mutate({ clientId });
      setShowResyncConfirm(false);
    }
  };

  return (
    <div className="flex items-center justify-end">
      <div className="flex items-center gap-2">
        {clientId && (
          <>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowResyncConfirm(true)}
              disabled={resyncVacanciesMutation.isPending}
            >
              <Icons.SyncIcon
                fontSize={16}
                className={
                  resyncVacanciesMutation.isPending ? "animate-spin" : ""
                }
              />
              {resyncVacanciesMutation.isPending
                ? t("common.resyncing")
                : t("integrations.indeed.jobSync.syncOverview.resync")}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                repairVacanciesMutation.mutate({
                  clientId,
                  syncState: syncStateFilter,
                });
              }}
              disabled={repairVacanciesMutation.isPending}
            >
              <Icons.SyncIcon
                fontSize={16}
                className={
                  repairVacanciesMutation.isPending ? "animate-spin" : ""
                }
              />
              {repairVacanciesMutation.isPending
                ? t("common.repairing")
                : t("integrations.indeed.jobSync.syncOverview.repair")}
            </Button>
          </>
        )}
        <AlertDialog open={showResyncConfirm} onOpenChange={setShowResyncConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("integrations.indeed.jobSync.syncOverview.resyncConfirm.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("integrations.indeed.jobSync.syncOverview.resyncConfirm.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleResyncConfirm}
                disabled={resyncVacanciesMutation.isPending}
              >
                {t("integrations.indeed.jobSync.syncOverview.resyncConfirm.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Select
          value={syncStateFilter !== null ? String(syncStateFilter) : "all"}
          onValueChange={handleSyncStateChange}
        >
          <SelectTrigger className="w-48">
            <SelectValue
              placeholder={t(
                "integrations.indeed.jobSync.syncOverview.filterByState"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value={String(VacancySyncState.Synced)}>
              {t("integrations.indeed.jobSync.vacancies.states.synced")}
            </SelectItem>
            <SelectItem value={String(VacancySyncState.Removed)}>
              {t("integrations.indeed.jobSync.vacancies.states.removed")}
            </SelectItem>
            <SelectItem value={String(VacancySyncState.NotSynced)}>
              {t("integrations.indeed.jobSync.vacancies.states.notSynced")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
